import React, { useEffect, useState } from 'react';
import { styled } from 'linaria/react';
import t from '@jetshop/intl';
import { theme } from '../Theme';
import { ElevateFacetsFlyout } from './facets/ElevateFacetsFlyout';
import { ElevateFacetsFlyoutSkeleton } from './facets/ElevateFacetsFlyoutSkeleton';
import { ElevateProductCard } from './ElevateProductCard';
import { ElevateGridPagination } from './pagination/ElevateGridPagination';
import { ElevateSkeletonProductCard } from './ElevateSkeletonProductCard';
import { useElevate } from './ElevateContext';
import { useHistory } from 'react-router-dom';

const ElevateGridList = styled('ul')`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: auto;
  padding: 12px;
  max-width: 90rem;
  column-gap: 1rem;
  row-gap: 3rem;
  margin: 0 auto;

  ${theme.below.xl} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${theme.below.md} {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 0.5rem;
    row-gap: 2rem;
    padding: 0 0.5rem;
  }
`;

const EmptyState = styled('li')`
  padding-block: 2rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 18px;
    font-weight: 500;
  }

  p {
    margin: 0;
    font-size: 16px;
    color: ${theme.colors.grey};
    line-height: 1.4;
  }
`;

const SKELETON_TIMEOUT_MS = 4000;

export const ElevateGrid = ({
  page = '',
  pageData,
  clickCallback,
  inViewCallback
}) => {
  const skeletonArray = Array.from({ length: 24 });
  const [skeletonTimedOut, setSkeletonTimedOut] = useState(false);

  const {
    productGroups,
    secondaryProductGroups,
    totalHits,
    facets,
    sortOrder,
    listName
  } = pageData;

  const isLoading = productGroups == null;
  const showSkeletons = isLoading && !skeletonTimedOut;

  let showFacetsFlyout = true;

  const history = useHistory();
  const { queryStringToObject } = useElevate();
  const currentParams = queryStringToObject(history.location.search);
  const hasFacetParams =
    Object.keys(currentParams).filter(
      key => key.startsWith('f.') || key.startsWith('c.')
    ).length > 0;

  if (page !== 'search') {
    if ((!totalHits || totalHits <= 0) && !hasFacetParams)
      showFacetsFlyout = false;
  }

  useEffect(() => {
    if (!isLoading) {
      setSkeletonTimedOut(false);
      return;
    }
    // ponytail: hides skeletons if fetch never resolves
    const id = setTimeout(() => setSkeletonTimedOut(true), SKELETON_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [isLoading]);

  const groupData =
    productGroups?.length > 0 ? productGroups : secondaryProductGroups;

  return (
    <>
      {showSkeletons ? (
        <ElevateFacetsFlyoutSkeleton />
      ) : (
        <>
          {showFacetsFlyout && (
            <ElevateFacetsFlyout
              page={page}
              facets={facets}
              sort={sortOrder}
              totalHits={totalHits}
              originalTerm={pageData?.autoCorrect?.q}
            />
          )}
        </>
      )}

      <ElevateGridList>
        {totalHits === 0 && showFacetsFlyout && !showSkeletons && (
          <EmptyState>
            <h2>{t('No products match your search')}</h2>
            <p>
              {t(
                'Try changing your filter settings or search term to see matching products.'
              )}
            </p>
          </EmptyState>
        )}
        {showSkeletons
          ? skeletonArray.map((_, index) => (
              <ElevateSkeletonProductCard key={index} />
            ))
          : groupData?.map((productGroup, index) => (
              <ElevateProductCard
                key={productGroup?.key + '-' + index}
                productGroups={productGroup}
                listName={listName}
                index={index}
                clickCallback={clickCallback}
                inViewCallback={inViewCallback}
              />
            ))}
      </ElevateGridList>
      {totalHits > 0 && groupData?.length > 0 && (
        <ElevateGridPagination totalHits={totalHits} />
      )}
    </>
  );
};
